import { getServerURL } from './config';
import { authService } from './authService';

const API_BASE_URL = getServerURL();

class UsersService {
  /**
   * Get all users
   */
  async getAllUsers(filters = {}) {
    try {
      const queryParams = new URLSearchParams();

      if (filters.search) queryParams.append('search', filters.search);
      if (filters.group) queryParams.append('group', filters.group);
      if (filters.page) queryParams.append('page', filters.page);
      if (filters.limit) queryParams.append('limit', filters.limit);

      const url = `${API_BASE_URL}/users${queryParams.toString() ? '?' + queryParams.toString() : ''}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: authService.getAuthHeaders(),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch users');
      }

      const result = await response.json();
      return result.data || result;
    } catch (error) {
      console.error('Get users error:', error);
      throw error;
    }
  }

  /**
   * Get a single user by ID
   */
  async getUser(userId) {
    try {
      const response = await fetch(`${API_BASE_URL}/users/${userId}`, {
        method: 'GET',
        headers: authService.getAuthHeaders(),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch user');
      }

      const result = await response.json();
      return result.data || result;
    } catch (error) {
      console.error('Get user error:', error);
      throw error;
    }
  }

  /**
   * Create a new user
   */
  async createUser(userData) {
    try {
      const response = await fetch(`${API_BASE_URL}/users`, {
        method: 'POST',
        headers: authService.getAuthHeaders(),
        body: JSON.stringify(userData),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create user');
      }

      const result = await response.json();
      return result.data || result;
    } catch (error) {
      console.error('Create user error:', error);
      throw error;
    }
  }

  /**
   * Update a user
   */
  async updateUser(userId, userData) {
    try {
      const response = await fetch(`${API_BASE_URL}/users/${userId}`, {
        method: 'PUT',
        headers: authService.getAuthHeaders(),
        body: JSON.stringify(userData),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update user');
      }

      const result = await response.json();
      return result.data || result;
    } catch (error) {
      console.error('Update user error:', error);
      throw error;
    }
  }

  /**
   * Delete a user (soft delete)
   */
  async deleteUser(userId) {
    try {
      const response = await fetch(`${API_BASE_URL}/users/${userId}`, {
        method: 'DELETE',
        headers: authService.getAuthHeaders(),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete user');
      }

      const result = await response.json();
      return result.data || result;
    } catch (error) {
      console.error('Delete user error:', error);
      throw error;
    }
  }

  /**
   * Change user password
   */
  async changeUserPassword(userId, newPassword) {
    try {
      const response = await fetch(`${API_BASE_URL}/users/${userId}/password`, {
        method: 'PUT',
        headers: authService.getAuthHeaders(),
        body: JSON.stringify({ password: newPassword }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to change password');
      }

      const result = await response.json();
      return result.data || result;
    } catch (error) {
      console.error('Change password error:', error);
      throw error;
    }
  }

  /**
   * Get all groups (for dropdown)
   */
  async getAllGroups() {
    try {
      const response = await fetch(`${API_BASE_URL}/groups`, {
        method: 'GET',
        headers: authService.getAuthHeaders(),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch groups');
      }

      const result = await response.json();
      return result.data || result;
    } catch (error) {
      console.error('Get groups error:', error);
      throw error;
    }
  }

  /**
   * Get assignable users for ticket assignment (requires VIEW_TICKETS or ASSIGN_TICKETS permission)
   */
  async getAssignableUsers() {
    try {
      const response = await fetch(`${API_BASE_URL}/users/assignable`, {
        method: 'GET',
        headers: authService.getAuthHeaders(),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch assignable users');
      }

      const result = await response.json();
      return result.data || result;
    } catch (error) {
      console.error('Get assignable users error:', error);
      throw error;
    }
  }

  /**
   * Get user options for dropdown (formatted for Polaris Select component)
   */
  async getUserOptionsForAssignment() {
    try {
      const users = await this.getAssignableUsers();

      // Format users for Select component
      return users.map(user => ({
        label: `${user.name} - ${user.group_name} (${user.email})`,
        value: user._id.toString()
      }));
    } catch (error) {
      console.error('Error getting user options:', error);
      return [];
    }
  }

  /**
   * Format user for display (helper function)
   */
  formatUserForDisplay(user) {
    if (!user) return 'Unassigned';

    return {
      id: user._id || user.id,
      name: user.name || 'Unknown User',
      email: user.email || '',
      group: user.group_name || user.group || 'Unknown',
      displayText: `${user.name || 'Unknown'} (${user.group_name || user.group || 'Unknown'})`
    };
  }
}

export const usersService = new UsersService();
